import { INFO_LABEL, INFO_STRIP, fmt } from "./utils";
import { EmptyState } from "../../../components/UI/States";

const OPPS_TH =
  "whitespace-nowrap bg-[#5a5a5a] px-[10px] py-[7px] text-left text-xs text-white first:text-left dark:bg-slate-700 [&:not(:first-child)]:text-right";
const OPPS_TD =
  "border-b border-[color:var(--app-divider)] px-[10px] py-[6px] text-xs [&:not(:first-child)]:text-right";

const SectionHeading = ({ children }) => (
  <h3 className="mb-3 text-base font-semibold tracking-tight">{children}</h3>
);

const TABLE_WRAP = "app-divider overflow-x-auto rounded-xl border";

const ROW = "transition-colors hover:bg-[color:var(--app-hover)]";

// PG2 categories where the rep sold below the company margin (the proc's
// Color=1 rows), grouped by category and by customer.
const OpportunitiesTab = ({ rows, info }) => {
  if (!rows.length) {
    return <EmptyState compact title="No data returned." />;
  }

  const below = rows.filter(
    (r) => r["Color"] === 1 && (r["TotalSales"] || 0) > 0
  );

  if (!below.length) {
    return (
      <div className="px-5 py-16 text-center">
        <div className="mb-3 text-4xl" aria-hidden="true">🎉</div>
        <p className="text-base font-semibold text-emerald-700 dark:text-emerald-300">
          All product categories are at or above company margin targets.
        </p>
        <p className="app-muted mt-2 text-sm">
          Great work, {info.repName}!
        </p>
      </div>
    );
  }

  const pg2Map = {};
  below.forEach((r) => {
    const key = r["PG2ID"];
    if (!pg2Map[key]) {
      pg2Map[key] = {
        name: r["PG2"],
        pg1: r["PG1"],
        sales: 0,
        gapSum: 0,
        count: 0,
        target: r["PG2 Company"] || 0,
      };
    }
    const g = pg2Map[key];
    g.sales += r["TotalSales"] || 0;
    g.gapSum += (r["PG2 Company"] || 0) - (r["TotalMargin"] || 0);
    g.count += 1;
  });
  const pg2Rows = Object.values(pg2Map).sort((a, b) => b.sales - a.sales);

  const custMap = {};
  below.forEach((r) => {
    const cid = r["CustomerID"];
    if (!custMap[cid]) {
      custMap[cid] = { name: r["Customer"], type: r["Name"], sales: 0, items: 0 };
    }
    custMap[cid].sales += r["TotalSales"] || 0;
    custMap[cid].items += 1;
  });
  const custRows = Object.values(custMap).sort((a, b) => b.sales - a.sales);

  const affectedSales = below.reduce((s, r) => s + (r["TotalSales"] || 0), 0);

  return (
    <div>
      <div className={`${INFO_STRIP} !mb-5`}>
        <span>
          <strong className={INFO_LABEL}>Rep:</strong> {info.repName}
        </span>
        <span>
          <strong className={INFO_LABEL}>Below-margin categories:</strong> {pg2Rows.length}
        </span>
        <span>
          <strong className={INFO_LABEL}>Affected sales:</strong> {fmt.dollars(affectedSales)}
        </span>
      </div>

      <div className="mb-6">
        <SectionHeading>Below-Target Product Categories</SectionHeading>
        <div className={TABLE_WRAP}>
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={OPPS_TH}>Product Group</th>
                <th className={OPPS_TH}>Category</th>
                <th className={OPPS_TH}>Sales at Risk</th>
                <th className={OPPS_TH}>Customers</th>
                <th className={OPPS_TH}>Target Margin</th>
                <th className={OPPS_TH}>Avg Gap</th>
              </tr>
            </thead>
            <tbody>
              {pg2Rows.map((p, i) => (
                <tr
                  key={i}
                  className={ROW}
                >
                  <td className={OPPS_TD}>{p.pg1}</td>
                  <td className={OPPS_TD}>{p.name}</td>
                  <td className={`${OPPS_TD} tabular-nums`}>
                    {fmt.dollars(p.sales)}
                  </td>
                  <td className={OPPS_TD}>{p.count}</td>
                  <td className={`${OPPS_TD} tabular-nums`}>
                    {fmt.pct(p.target)}
                  </td>
                  <td className={OPPS_TD}>
                    <span className="inline-block rounded-full bg-[#ff9999] px-[7px] py-[1px] text-[11px] font-bold text-[#700000] dark:bg-red-500/30 dark:text-red-200">
                      -{(p.gapSum / p.count).toFixed(2)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <SectionHeading>Customers With Below-Target Categories</SectionHeading>
        <div className={TABLE_WRAP}>
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={OPPS_TH}>Customer</th>
                <th className={OPPS_TH}>Type</th>
                <th className={OPPS_TH}>Sales in Below-Margin Categories</th>
                <th className={OPPS_TH}>Categories Below Target</th>
              </tr>
            </thead>
            <tbody>
              {custRows.map((c, i) => (
                <tr
                  key={i}
                  className={ROW}
                >
                  <td className={OPPS_TD}>{c.name}</td>
                  <td className={`${OPPS_TD} app-muted`}>{c.type}</td>
                  <td className={`${OPPS_TD} tabular-nums`}>
                    {fmt.dollars(c.sales)}
                  </td>
                  <td className={OPPS_TD}>{c.items}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OpportunitiesTab;
